import React, { useState } from "react";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";

import tallargeSunset from "../assets/tallarge_sunset.png";
import tallargeNightCar from "../assets/tallarge_night_car.png";
import lineQr from "../assets/M_gainfriends_2dbarcodes_GW.png";

const DEMO_REQUEST_ENDPOINT =
  "https://asia-east1-chat3test-59045.cloudfunctions.net/submitDemoRequest";

function ArrowIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m14 6 6 6-6 6" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.78.62 2.63a2 2 0 0 1-.45 2.11L8 9.73a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.85.29 1.73.5 2.63.62A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function scrollToSection(id) {
  const element = document.getElementById(id);

  if (!element) return;

  element.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

export default function Contact() {
  const [heroMode, setHeroMode] = useState("contact");

  const [formValues, setFormValues] = useState({
    name: "",
    phone: "",
    companyName: "",
    email: "",
    fleetSize: "",
    preferredContactTime: "",
    note: "",
  });

  const [selectedOperations, setSelectedOperations] =
    useState([]);

  const [selectedInterests, setSelectedInterests] =
    useState([]);

  const [otherOperation, setOtherOperation] =
    useState("");

  const [operationError, setOperationError] =
    useState("");

  const [consentAccepted, setConsentAccepted] =
    useState(false);

  const [isConsentModalOpen, setIsConsentModalOpen] =
    useState(false);

  const [demoHoneypot, setDemoHoneypot] =
    useState("");

  const [submitState, setSubmitState] = useState({
    status: "idle",
    message: "",
  });

  const switchSection = (mode) => {
    const target =
      mode === "contact"
        ? "contact-info"
        : "book-demo";

    if (heroMode === mode) {
      scrollToSection(target);
      return;
    }

    setHeroMode(mode);

    // 先讓使用者看到 Hero 圖片與文字的切換，
    // 再慢慢滑到對應內容。
    window.setTimeout(() => {
      scrollToSection(target);
    }, 520);
  };

  const toggleOperation = (value) => {
    setSelectedOperations((current) => {
      const exists =
        current.includes(value);

      if (exists) {
        if (value === "other") {
          setOtherOperation("");
        }

        return current.filter(
          (item) => item !== value
        );
      }

      return [
        ...current,
        value,
      ];
    });

    setOperationError("");
  };

  const updateFormValue = (field, value) => {
    setFormValues((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const toggleInterest = (value) => {
    setSelectedInterests((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value],
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitState.status === "submitting") return;

    if (
      selectedOperations.length === 0
    ) {
      setOperationError(
        "請至少選擇一項目前主要作業型態"
      );

      return;
    }

    if (
      selectedOperations.includes(
        "other"
      ) &&
      !otherOperation.trim()
    ) {
      setOperationError(
        "請填寫其他運輸／物流類型"
      );

      return;
    }

    setOperationError("");

    if (!consentAccepted) {
      setIsConsentModalOpen(true);
      return;
    }

    setSubmitState({
      status: "submitting",
      message: "正在送出預約資料…",
    });

    try {
      const response = await fetch(DEMO_REQUEST_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formValues.name,
          phone: formValues.phone,
          companyName: formValues.companyName,
          email: formValues.email,
          operations: selectedOperations,
          otherOperation: otherOperation.trim(),
          fleetSize: formValues.fleetSize,
          preferredContactTime: formValues.preferredContactTime,
          interests: selectedInterests,
          note: formValues.note,
          consentAccepted,
          website: demoHoneypot,
          pagePath: window.location.pathname,
          referrer: document.referrer,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.ok || result.accepted !== true) {
        throw new Error(result.message || "預約送出失敗，請稍後再試。 ");
      }

      setSubmitState({
        status: "success",
        message: "預約已送出，我們會盡快與您聯繫。",
      });
      setFormValues({
        name: "",
        phone: "",
        companyName: "",
        email: "",
        fleetSize: "",
        preferredContactTime: "",
        note: "",
      });
      setSelectedOperations([]);
      setSelectedInterests([]);
      setOtherOperation("");
      setConsentAccepted(false);
      setDemoHoneypot("");
    } catch (error) {
      console.error("[Contact] demo request error:", error);
      setSubmitState({
        status: "error",
        message: error?.message || "預約送出失敗，請稍後再試。",
      });
    }
  };

  return (
    <main className="contact-page">
      <style>{styles}</style>

      <SiteHeader />

      {/* =====================================================
          HERO
         ===================================================== */}

      <section className="contact-hero">
        <div className="hero-images">
          <img
            src={tallargeSunset}
            alt="Tallarge 車隊物流場域"
            className={`hero-image ${
              heroMode === "contact"
                ? "active"
                : ""
            }`}
          />

          <img
            src={tallargeNightCar}
            alt="Tallarge 夜間運輸"
            className={`hero-image ${
              heroMode === "demo"
                ? "active"
                : ""
            }`}
          />
        </div>

        <div className="hero-overlay" />

        <div className="contact-wrap hero-content">
          <div className="hero-text-stage">
            <div
              className={`hero-text ${
                heroMode === "contact"
                  ? "active"
                  : ""
              }`}
            >
              <h1>
                聯絡我們
              </h1>

              <p>
                告訴我們您的需求，我們會盡快與您聯繫，
                <br />
                一起找到更適合的運作方式。
              </p>
            </div>

            <div
              className={`hero-text ${
                heroMode === "demo"
                  ? "active"
                  : ""
              }`}
            >
              <h1>
                預約展示
              </h1>

              <p>
                讓我們更了解您的需求，
                <br />
                安排專人為您進行產品介紹與展示。
              </p>
            </div>
          </div>

          <div className="hero-switch">
            <button
              type="button"
              className={
                heroMode === "contact"
                  ? "active"
                  : ""
              }
              onClick={() =>
                switchSection(
                  "contact"
                )
              }
            >
              聯絡我們
            </button>

            <button
              type="button"
              className={
                heroMode === "demo"
                  ? "active"
                  : ""
              }
              onClick={() =>
                switchSection(
                  "demo"
                )
              }
            >
              預約展示
            </button>
          </div>
        </div>
      </section>

      {/* =====================================================
          CONTACT
         ===================================================== */}

      <section
        id="contact-info"
        className="contact-section"
      >
        <div className="contact-wrap contact-layout">
          <div className="section-heading">

            <h2>
              聯絡我們
            </h2>
          </div>

          <div className="contact-details">

            {/* LINE */}

            <div className="contact-line-row contact-line-main">
              <div className="contact-line-content">
                <div className="line-mark">
                  LINE
                </div>

                <div>
                  <span className="contact-label">
                    LINE 官方帳號
                  </span>

                  <a
                    href="https://line.me/R/ti/p/%40764lebbw"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-button"
                  >
                    加入好友
                    <ArrowIcon size={14} />
                  </a>
                </div>
              </div>

              <a
                href="https://line.me/R/ti/p/%40764lebbw"
                target="_blank"
                rel="noopener noreferrer"
                className="line-qr-link"
                aria-label="加入 Tallarge LINE 官方帳號"
              >
                <img
                  src={lineQr}
                  alt="Tallarge LINE 官方帳號 QR Code"
                  className="line-qr-image"
                  draggable="false"
                />
              </a>
            </div>

            {/* PHONE */}

            <div className="contact-line-row">
              <div className="contact-simple-icon">
                <PhoneIcon />
              </div>

              {/* <div className="contact-info-copy">
                <span className="contact-label">
                  聯絡電話
                </span>

                <a
                  href="tel:077654321"
                  className="contact-value"
                >
                  07-7654321
                </a>
              </div> */}
            </div>

            {/* EMAIL */}

            <div className="contact-line-row">
              <div className="contact-simple-icon">
                <MailIcon />
              </div>

              <div className="contact-info-copy">
                <span className="contact-label">
                  電子郵件
                </span>

                <a
                  href="mailto:tallarge.tw@gmail.com"
                  className="contact-value contact-email"
                >
                  tallarge.tw@gmail.com
                </a>
              </div>
            </div>

            {/* TIME */}

            <div className="contact-line-row">
              <div className="contact-simple-icon">
                <ClockIcon />
              </div>

              <div className="contact-info-copy">
                <span className="contact-label">
                  服務時間
                </span>

                <span className="contact-value">
                  08:30 ～ 17:30
                </span>

                <p>
                  週一至週五，國定假日除外。
                </p>
              </div>
            </div>

          </div>
        </div>

        <div className="contact-wrap contact-demo-callout">
          <div>
            <h3>
              想進一步了解 Tallarge？
            </h3>

            <p>
              如果希望看看 Tallarge
              實際如何配合車隊營運，
              可以直接預約產品展示。
            </p>
          </div>

          <button
            type="button"
            onClick={() =>
              switchSection(
                "demo"
              )
            }
          >
            預約產品展示
            <ArrowIcon />
          </button>
        </div>
      </section>

      {/* =====================================================
          DEMO
         ===================================================== */}

      <section
        id="book-demo"
        className="demo-section"
      >
        <div className="contact-wrap demo-layout">

          {/* 左側：Web + App 動態展示 */}

          <div className="demo-intro">
            <h2>
              預約展示
            </h2>
          </div>

          {/* 右側表單 */}

          <form
            className="demo-form"
            onSubmit={handleSubmit}
          >
            <div className="form-heading">
              <h3>
                填寫預約資訊
              </h3>

              <p>
                送出後，我們會盡快與您確認。
              </p>
            </div>

            <div className="form-grid">

              <label>
                <span>
                  姓名
                  <i>*</i>
                </span>

                <input
                  type="text"
                  name="name"
                  placeholder="請輸入姓名"
                  value={formValues.name}
                  onChange={(event) =>
                    updateFormValue("name", event.target.value)
                  }
                  required
                />
              </label>

              <label>
                <span>
                  聯絡電話
                  <i>*</i>
                </span>

                <input
                  type="tel"
                  name="phone"
                  placeholder="請輸入聯絡電話"
                  value={formValues.phone}
                  onChange={(event) =>
                    updateFormValue("phone", event.target.value)
                  }
                  required
                />
              </label>

              <label>
                <span>
                  公司名稱
                  <i>*</i>
                </span>

                <input
                  type="text"
                  name="companyName"
                  placeholder="請輸入公司名稱"
                  value={formValues.companyName}
                  onChange={(event) =>
                    updateFormValue("companyName", event.target.value)
                  }
                  required
                />
              </label>

              <label>
                <span>
                  電子信箱
                  <i>*</i>
                </span>

                <input
                  type="email"
                  name="email"
                  placeholder="請輸入電子信箱"
                  value={formValues.email}
                  onChange={(event) =>
                    updateFormValue("email", event.target.value)
                  }
                  required
                />
              </label>

              {/* =========================
                  主要作業範圍
                 ========================= */}

              <fieldset className="operation-field form-full">
                <legend>
                  主要作業範圍
                  <i>*</i>

                  <small>
                    （可複選）
                  </small>
                </legend>

                <div className="operation-options">
                  {[
                    [
                      "container",
                      "港區／貨櫃運輸",
                    ],
                    [
                      "cold-chain",
                      "冷鏈／溫控運輸",
                    ],
                    [
                      "project",
                      "工程／專案運輸",
                    ],
                    [
                      "warehouse-transfer",
                      "倉儲／轉運作業",
                    ],
                    [
                      "other",
                      "其他",
                    ],
                  ].map(
                    ([
                      value,
                      label,
                    ]) => (
                      <label
                        key={value}
                      >
                        <input
                          type="checkbox"
                          name="operation"
                          value={value}
                          checked={
                            selectedOperations.includes(
                              value
                            )
                          }
                          onChange={() =>
                            toggleOperation(
                              value
                            )
                          }
                        />

                        <span>
                          {label}
                        </span>
                      </label>
                    )
                  )}
                </div>

                {selectedOperations.includes(
                  "other"
                ) && (
                  <input
                    type="text"
                    className="operation-other-input"
                    value={
                      otherOperation
                    }
                    onChange={(
                      event
                    ) => {
                      setOtherOperation(
                        event.target.value
                      );

                      setOperationError(
                        ""
                      );
                    }}
                    placeholder="請輸入其他運輸／物流類型"
                    maxLength="80"
                    required
                  />
                )}

                {operationError && (
                  <p className="operation-error">
                    {operationError}
                  </p>
                )}
              </fieldset>

              <label>
                <span>
                  車隊規模
                  <i>*</i>
                </span>

                <select
                  name="fleetSize"
                  value={formValues.fleetSize}
                  onChange={(event) =>
                    updateFormValue("fleetSize", event.target.value)
                  }
                  required
                >
                  <option
                    value=""
                    disabled
                  >
                    請選擇車隊規模
                  </option>

                  <option value="1-5">
                    1–5 輛
                  </option>

                  <option value="6-20">
                    6–20 輛
                  </option>

                  <option value="21-50">
                    21–50 輛
                  </option>

                  <option value="51-100">
                    51–100 輛
                  </option>

                  <option value="100+">
                    100 輛以上
                  </option>
                </select>
              </label>

              <label className="form-full">
                <span>
                  方便聯絡時間
                  <i>*</i>
                </span>

                <select
                  name="preferredContactTime"
                  value={formValues.preferredContactTime}
                  onChange={(event) =>
                    updateFormValue("preferredContactTime", event.target.value)
                  }
                  required
                >
                  <option
                    value=""
                    disabled
                  >
                    請選擇方便聯絡的時段
                  </option>

                  <option value="weekday-morning">
                    平日上午 08:30－12:00
                  </option>

                  <option value="weekday-afternoon">
                    平日下午 13:00－17:30
                  </option>

                  <option value="flexible">
                    時間彈性，可再與我確認
                  </option>

                </select>
              </label>
            </div>

            {/* =================================================
                改善項目
               ================================================= */}

            <fieldset className="interest-field">
              <legend>
                目前最想改善的項目

                <small>
                  （可複選）
                </small>
              </legend>

              <div className="interest-options">
                <label>
                  <input
                    type="checkbox"
                    name="interest"
                    value="fragmented-information"
                    checked={selectedInterests.includes("fragmented-information")}
                    onChange={() => toggleInterest("fragmented-information")}
                  />

                  <span>
                    資訊分散
                  </span>
                </label>

                <label>
                  <input
                    type="checkbox"
                    name="interest"
                    value="coordination"
                    checked={selectedInterests.includes("coordination")}
                    onChange={() => toggleInterest("coordination")}
                  />

                  <span>
                    統籌困難
                  </span>
                </label>

                <label>
                  <input
                    type="checkbox"
                    name="interest"
                    value="dispatch"
                    checked={selectedInterests.includes("dispatch")}
                    onChange={() => toggleInterest("dispatch")}
                  />

                  <span>
                    任務調度
                  </span>
                </label>

                <label>
                  <input
                    type="checkbox"
                    name="interest"
                    value="tracking"
                    checked={selectedInterests.includes("tracking")}
                    onChange={() => toggleInterest("tracking")}
                  />

                  <span>
                    即時追蹤
                  </span>
                </label>

                <label>
                  <input
                    type="checkbox"
                    name="interest"
                    value="fleet"
                    checked={selectedInterests.includes("fleet")}
                    onChange={() => toggleInterest("fleet")}
                  />

                  <span>
                    人車管理
                  </span>
                </label>

                <label>
                  <input
                    type="checkbox"
                    name="interest"
                    value="report"
                    checked={selectedInterests.includes("report")}
                    onChange={() => toggleInterest("report")}
                  />

                  <span>
                    報表整理
                  </span>
                </label>

                <label>
                  <input
                    type="checkbox"
                    name="interest"
                    value="other"
                    checked={selectedInterests.includes("other")}
                    onChange={() => toggleInterest("other")}
                  />

                  <span>
                    其他
                  </span>
                </label>
              </div>
            </fieldset>

            <label className="textarea-field">
              <span>
                需求補充
              </span>

              <textarea
                name="note"
                rows="5"
                maxLength="500"
                placeholder="請簡述目前的營運需求，或任何想進一步了解的內容。"
                value={formValues.note}
                onChange={(event) =>
                  updateFormValue("note", event.target.value)
                }
              />
            </label>

            <input
              type="text"
              name="website"
              value={demoHoneypot}
              onChange={(event) => setDemoHoneypot(event.target.value)}
              tabIndex="-1"
              autoComplete="off"
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-10000px",
                width: "1px",
                height: "1px",
                opacity: 0,
                pointerEvents: "none",
              }}
            />

            <div className="form-submit-row">
              {submitState.message && (
                <p
                  role="status"
                  style={{
                    margin: "0 auto 0 0",
                    color: submitState.status === "error" ? "#b14c3f" : "#3e8061",
                    fontSize: "12px",
                    lineHeight: 1.5,
                  }}
                >
                  {submitState.message}
                </p>
              )}
              <button
                type="submit"
                className="submit-button"
                disabled={submitState.status === "submitting"}
              >
                {submitState.status === "submitting" ? "送出中…" : "送出預約"}
                <ArrowIcon />
              </button>
            </div>
          </form>

          {isConsentModalOpen && (
            <div
              className="consent-modal-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  setIsConsentModalOpen(false);
                }
              }}
            >
              <div
                className="consent-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="consent-modal-title"
              >
                <div className="consent-modal-header">
                  <div>
                    <span className="consent-modal-kicker">送出前確認</span>
                    <h3 id="consent-modal-title">確認預約資料</h3>
                  </div>
                  <button
                    type="button"
                    className="consent-modal-close"
                    aria-label="關閉確認視窗"
                    onClick={() => setIsConsentModalOpen(false)}
                  >
                    ×
                  </button>
                </div>

                <div className="consent-summary">
                  <div>
                    <span>姓名*</span>
                    <strong>{formValues.name || "—"}</strong>
                  </div>
                  <div>
                    <span>聯絡電話*</span>
                    <strong>{formValues.phone || "—"}</strong>
                  </div>
                  <div>
                    <span>公司名稱*</span>
                    <strong>{formValues.companyName || "—"}</strong>
                  </div>
                  <div>
                    <span>電子信箱*</span>
                    <strong>{formValues.email || "—"}</strong>
                  </div>
                </div>

                <label className="consent-modal-row">
                  <input
                    type="checkbox"
                    checked={consentAccepted}
                    onChange={(event) => setConsentAccepted(event.target.checked)}
                  />
                  <span>
                    我已閱讀並同意 Tallarge 的{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer">
                      隱私權政策
                    </a>
                    {" "}與{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer">
                      使用條款
                    </a>
                    ，並同意 Tallarge 使用我提供的資訊與我聯繫及安排產品展示。
                  </span>
                </label>

                <div className="consent-modal-actions">
                  <button
                    type="button"
                    className="consent-modal-cancel"
                    onClick={() => setIsConsentModalOpen(false)}
                  >
                    返回修改
                  </button>
                  <button
                    type="button"
                    className="consent-modal-confirm"
                    disabled={!consentAccepted}
                    onClick={() => {
                      setIsConsentModalOpen(false);
                      window.setTimeout(() => {
                        document.querySelector(".demo-form")?.requestSubmit();
                      }, 0);
                    }}
                  >
                    同意並送出
                    <ArrowIcon size={15} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* =====================================================
          FOOTER
         ===================================================== */}

      <SiteFooter />
    </main>
  );
}

const styles = `
:root {
  --ink: #18212b;
  --sub: #727b84;
  --line: #e8e8e6;
  --paper: #ffffff;
  --soft: #f7f7f5;
  --orange: #ff7518;
  --orange-dark: #e96008;
  --ease: cubic-bezier(.23, 1, .32, 1);
}

* {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
  scroll-padding-top: 60px;
}

.contact-page {
  margin: 0;

  background:
    var(--paper);

  color:
    var(--ink);

  font-family:
    -apple-system,
    BlinkMacSystemFont,
    "SF Pro Display",
    "SF Pro Text",
    "PingFang TC",
    "Noto Sans TC",
    "Microsoft JhengHei",
    Arial,
    sans-serif;

  -webkit-font-smoothing:
    antialiased;
}

button,
input,
select,
textarea {
  font: inherit;
}

button {
  border: 0;
}

.contact-wrap {
  width:
    min(
      1180px,
      calc(
        100% - 48px
      )
    );

  margin:
    0 auto;
}


/* =========================================================
   HERO
   ========================================================= */

.contact-hero {
  position: relative;

  height:
    clamp(
      390px,
      54vh,
      540px
    );

  min-height:
    390px;

  overflow:
    hidden;

  background:
    #16202b;
}


/* ---------- Images ---------- */

.hero-images {
  position: absolute;

  inset: 0;

  overflow:
    hidden;
}

.hero-image {
  position: absolute;

  inset: 0;

  width: 100%;
  height: 100%;

  object-fit:
    cover;

  object-position:
    center;

  opacity: 0;

  transform:
    scale(
      1.045
    );

  transition:
    opacity .85s ease,
    transform 1.5s var(--ease);

  will-change:
    opacity,
    transform;
}

.hero-image.active {
  opacity: 1;

  transform:
    scale(1);
}

.hero-image:nth-child(2) {
  object-position:
    center 55%;
}


/* ---------- Overlay ---------- */

.hero-overlay {
  position: absolute;

  inset: 0;

  z-index: 1;

  background:
    linear-gradient(
      90deg,
      rgba(
        5,
        12,
        20,
        .68
      ) 0%,
      rgba(
        5,
        12,
        20,
        .48
      ) 31%,
      rgba(
        5,
        12,
        20,
        .16
      ) 58%,
      rgba(
        5,
        12,
        20,
        .03
      ) 100%
    );
}


/* ---------- Content ---------- */

.hero-content {
  position: relative;

  z-index: 2;

  height: 100%;

  display: flex;

  flex-direction:
    column;

  justify-content:
    center;

  align-items:
    flex-start;
}


/* ---------- Text animation ---------- */

.hero-text-stage {
  position: relative;

  width:
    min(
      590px,
      62%
    );

  min-height:
    145px;
}

.hero-text {
  position:
    absolute;

  top: 0;
  left: 0;

  width: 100%;

  opacity: 0;

  transform:
    translateY(
      15px
    );

  pointer-events:
    none;

  transition:
    opacity .5s ease,
    transform .65s var(--ease);
}

.hero-text.active {
  opacity: 1;

  transform:
    translateY(
      0
    );

  pointer-events:
    auto;
}

.hero-text h1 {
  margin: 0;

  color:
    #fff;

  font-size:
    clamp(
      42px,
      4.2vw,
      62px
    );

  font-weight:
    590;

  line-height:
    1.06;

  letter-spacing:
    -.055em;

  text-shadow:
    0 3px 24px
    rgba(
      0,
      0,
      0,
      .16
    );
}

.hero-text p {
  max-width:
    520px;

  margin:
    19px 0 0;

  color:
    rgba(
      255,
      255,
      255,
      .82
    );

  font-size:
    clamp(
      14px,
      1.15vw,
      17px
    );

  line-height:
    1.75;

  letter-spacing:
    -.012em;

  text-shadow:
    0 2px 16px
    rgba(
      0,
      0,
      0,
      .16
    );
}


/* ---------- Minimal tabs ---------- */

.hero-switch {
  display: flex;

  align-items:
    center;

  gap:
    24px;

  margin-top:
    24px;
}

.hero-switch button {
  position: relative;

  padding:
    7px 0;

  background:
    transparent;

  color:
    rgba(
      255,
      255,
      255,
      .6
    );

  cursor:
    pointer;

  font-size:
    13px;

  font-weight:
    520;

  transition:
    color .25s ease;
}

.hero-switch button::after {
  content: "";

  position:
    absolute;

  right: 0;
  bottom: 0;
  left: 0;

  height: 1px;

  background:
    #fff;

  transform:
    scaleX(
      0
    );

  transform-origin:
    right center;

  transition:
    transform .4s var(--ease);
}

.hero-switch button:hover {
  color:
    #fff;
}

.hero-switch button.active {
  color:
    #fff;
}

.hero-switch button.active::after {
  transform:
    scaleX(
      1
    );

  transform-origin:
    left center;
}


/* =========================================================
   SECTION SHARED
   ========================================================= */

.section-line {
  display:
    block;

  width:
    28px;

  height:
    2px;

  margin-bottom:
    18px;

  background:
    var(--orange);
}

.section-heading h2,
.demo-intro > h2 {
  margin: 0;

  color:
    #20252a;

  font-size:
    clamp(
      34px,
      3.3vw,
      50px
    );

  font-weight:
    610;

  line-height:
    1.1;

  letter-spacing:
    -.055em;
}

.section-heading > p,
.demo-intro > p {
  max-width:
    460px;

  margin:
    18px 0 0;

  color:
    var(--sub);

  font-size:
    14px;

  line-height:
    1.8;
}


/* =========================================================
   CONTACT
   ========================================================= */

.contact-section {
  padding:
    clamp(
      72px,
      9vw,
      120px
    )
    0
    clamp(
      70px,
      8vw,
      105px
    );
}

.contact-layout {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
}

.section-heading {
  width: 100%;
  text-align: center;
}

.contact-details {
  width: min(100%, 720px);
  border-top:
    1px solid
    var(--line);
}

.contact-line-row {
  min-height:
    116px;

  display:
    grid;

  grid-template-columns:
    54px
    1fr;

  align-items:
    center;

  gap:
    21px;

  padding:
    24px 0;

  border-bottom:
    1px solid
    var(--line);
}

.contact-line-main {
  grid-template-columns:
    1fr
    128px;

  min-height:
    180px;
}

.contact-line-content {
  display:
    grid;

  grid-template-columns:
    54px
    1fr;

  gap:
    21px;

  align-items:
    start;
}

.line-mark {
  width:
    46px;

  height:
    46px;

  display:
    grid;

  place-items:
    center;

  border-radius:
    12px;

  background:
    #17c84b;

  color:
    #fff;

  font-size:
    10px;

  font-weight:
    750;
}

.contact-label {
  display:
    block;

  color:
    #6f7880;

  font-size:
    12px;
}

.contact-line-content h3 {
  margin:
    5px 0 0;

  color:
    #242a30;

  font-size:
    20px;

  font-weight:
    600;

  letter-spacing:
    -.025em;
}

.contact-line-content p,
.contact-info-copy p {
  max-width:
    440px;

  margin:
    7px 0 0;

  color:
    #818990;

  font-size:
    12px;

  line-height:
    1.65;
}

.text-button {
  display:
    inline-flex;

  align-items:
    center;

  gap:
    7px;

  margin-top:
    13px;

  padding: 0;

  background:
    transparent;

  color:
    #20262b;

  font-size:
    12px;

  font-weight:
    600;

  text-decoration:
    none;

  cursor:
    pointer;
}

.text-button:hover {
  color:
    var(--orange);
}


/* =========================================================
   LINE
   ========================================================= */

.line-account-id {
  display:
    block;

  margin-top:
    5px;

  color:
    #252b30;

  font-size:
    17px;

  font-weight:
    600;

  line-height:
    1.3;

  letter-spacing:
    -.02em;
}

.line-qr-link {
  width:
    112px;

  height:
    112px;

  display:
    block;

  padding:
    5px;

  overflow:
    hidden;

  border:
    1px solid
    #ececea;

  border-radius:
    10px;

  background:
    #fff;

  text-decoration:
    none;

  transition:
    border-color .18s ease,
    transform .18s ease;
}

.line-qr-link:hover {
  border-color:
    #d8d8d5;

  transform:
    translateY(
      -1px
    );
}

.line-qr-image {
  display:
    block;

  width:
    100%;

  height:
    100%;

  object-fit:
    contain;

  user-select:
    none;

  -webkit-user-drag:
    none;
}

.contact-simple-icon {
  width:
    45px;

  height:
    45px;

  display:
    grid;

  place-items:
    center;

  border-radius:
    50%;

  background:
    #f5f5f3;

  color:
    #313941;
}

.contact-info-copy {
  min-width:
    0;
}

.contact-value {
  display:
    block;

  margin-top:
    4px;

  color:
    #20252a;

  font-size:
    22px;

  font-weight:
    560;

  line-height:
    1.25;

  letter-spacing:
    -.02em;

  text-decoration:
    none;
}

.contact-email {
  font-size:
    18px;
}

.contact-demo-callout {
  display:
    flex;

  align-items:
    center;

  justify-content:
    space-between;

  gap:
    30px;

  margin-top:
    50px;

  padding-top:
    34px;

  border-top:
    1px solid
    var(--line);
}

.contact-demo-callout h3 {
  margin: 0;

  color:
    #24292e;

  font-size:
    21px;

  font-weight:
    600;
}

.contact-demo-callout p {
  margin:
    7px 0 0;

  color:
    var(--sub);

  font-size:
    13px;

  line-height:
    1.65;
}

.contact-demo-callout button {
  height:
    44px;

  display:
    inline-flex;

  align-items:
    center;

  gap:
    8px;

  flex:
    0 0 auto;

  padding:
    0 20px;

  border-radius:
    999px;

  background:
    var(--orange);

  color:
    #fff;

  cursor:
    pointer;

  font-size:
    13px;

  font-weight:
    600;
}


/* =========================================================
   DEMO
   ========================================================= */

.demo-section {
  padding:
    clamp(
      74px,
      9vw,
      120px
    )
    0;

  background:
    var(--soft);

  border-top:
    1px solid
    #ededeb;
}

.demo-layout {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 42px;
}


/* =========================================================
   DEMO PRODUCT VISUAL
   ========================================================= */

.demo-intro {
  min-width:
    0;
  width: 100%;
  text-align: center;
}

.demo-product-visual {
  position:
    relative;

  width:
    100%;

  height:
    clamp(
      390px,
      38vw,
      520px
    );

  margin-top:
    34px;

  perspective:
    1200px;
}

.demo-visual-shadow {
  position:
    absolute;

  left:
    10%;

  right:
    4%;

  bottom:
    6%;

  height:
    72px;

  border-radius:
    50%;

  background:
    rgba(
      30,
      39,
      48,
      .08
    );

  filter:
    blur(
      28px
    );

  pointer-events:
    none;
}

.demo-device {
  position:
    absolute;

  will-change:
    transform;
}


/* ---------- Web ---------- */

.demo-web-device {
  z-index:
    1;

  top:
    44px;

  right:
    0;

  width:
    80%;

  animation:
    demoWebFloat
    7.2s
    ease-in-out
    infinite;
}

.demo-web-shell {
  width:
    100%;

  aspect-ratio:
    16 / 10;

  overflow:
    hidden;

  border:
    1px solid
    rgba(
      31,
      39,
      47,
      .10
    );

  border-radius:
    15px;

  background:
    #fff;

  box-shadow:
    0 26px 60px
    rgba(
      25,
      34,
      43,
      .10
    );
}

.demo-browser-bar {
  height:
    28px;

  display:
    flex;

  align-items:
    center;

  gap:
    5px;

  padding:
    0 11px;

  border-bottom:
    1px solid
    #eceeed;

  background:
    #f7f7f5;
}

.demo-browser-bar span {
  width:
    6px;

  height:
    6px;

  border-radius:
    50%;

  background:
    #d5d7d6;
}

.demo-web-screen {
  width:
    100%;

  height:
    calc(
      100% - 28px
    );

  overflow:
    hidden;

  background:
    #fff;
}

.demo-web-image {
  display:
    block;

  width:
    100%;

  height:
    100%;

  object-fit:
    cover;

  object-position:
    top center;
}


/* ---------- Phones ---------- */

.demo-phone-device {
  z-index:
    3;
}

.demo-phone-left {
  left: 0;

  bottom:
    14px;

  width:
    38%;

  transform:
    rotate(
      -6deg
    );

  animation:
    demoPhoneLeftFloat
    6.4s
    ease-in-out
    infinite;
}

.demo-phone-right {
  right:
    4%;

  bottom:
    -2px;

  z-index:
    4;

  width:
    33%;

  transform:
    rotate(
      5deg
    );

  animation:
    demoPhoneRightFloat
    7.1s
    ease-in-out
    infinite;
}

.demo-phone-shell {
  position:
    relative;

  width:
    100%;

  aspect-ratio:
    9 / 18;

  padding:
    5px;

  overflow:
    hidden;

  border:
    3px solid
    #202326;

  border-radius:
    27px;

  background:
    #202326;

  box-shadow:
    0 24px 56px
    rgba(
      25,
      33,
      40,
      .15
    );
}

.demo-phone-speaker {
  position:
    absolute;

  z-index:
    3;

  top:
    8px;

  left:
    50%;

  width:
    48px;

  height:
    6px;

  transform:
    translateX(
      -50%
    );

  border-radius:
    20px;

  background:
    #111;
}


/* ---------- Empty screens ---------- */

.demo-empty-screen {
  width:
    100%;

  height:
    100%;

  background:
    linear-gradient(
      145deg,
      #fafaf9,
      #f1f2f0
    );
}

.demo-phone-shell
.demo-empty-screen {
  border-radius:
    20px;
}


/* ---------- Motion ---------- */

@keyframes demoWebFloat {
  0%,
  100% {
    transform:
      translate3d(
        0,
        0,
        0
      );
  }

  50% {
    transform:
      translate3d(
        0,
        -7px,
        0
      );
  }
}

@keyframes demoPhoneLeftFloat {
  0%,
  100% {
    transform:
      translate3d(
        0,
        0,
        0
      )
      rotate(
        -6deg
      );
  }

  50% {
    transform:
      translate3d(
        0,
        -9px,
        0
      )
      rotate(
        -5.4deg
      );
  }
}

@keyframes demoPhoneRightFloat {
  0%,
  100% {
    transform:
      translate3d(
        0,
        0,
        0
      )
      rotate(
        5deg
      );
  }

  50% {
    transform:
      translate3d(
        0,
        7px,
        0
      )
      rotate(
        4.4deg
      );
  }
}


/* ---------- Product visual tablet ---------- */

@media (max-width: 900px) {
  .demo-product-visual {
    width:
      min(
        620px,
        100%
      );

    height:
      460px;

    margin-top:
      28px;
  }

  .demo-web-device {
    width:
      74%;
  }

  .demo-phone-left {
    width:
      32%;
  }

  .demo-phone-right {
    width:
      29%;
  }
}


/* ---------- Product visual mobile ---------- */

@media (max-width: 620px) {
  .demo-product-visual {
    height:
      350px;

    margin-top:
      24px;
  }

  .demo-web-device {
    top:
      28px;

    right:
      -6%;

    width:
      83%;
  }

  .demo-phone-left {
    left:
      0;

    bottom:
      3px;

    width:
      36%;
  }

  .demo-phone-right {
    right:
      3%;

    bottom:
      -4px;

    width:
      31%;
  }

  .demo-phone-shell {
    padding:
      3px;

    border-width:
      2px;

    border-radius:
      20px;
  }

  .demo-phone-shell
  .demo-empty-screen {
    border-radius:
      16px;
  }

  .demo-phone-speaker {
    top:
      6px;

    width:
      34px;

    height:
      4px;
  }
}


/* =========================================================
   FORM
   ========================================================= */

.demo-form {
  width: min(100%, 760px);
  padding:
    0;
}

.form-heading {
  padding-bottom:
    24px;

  border-bottom:
    1px solid
    #dfdfdc;
}

.form-heading h3 {
  margin:
    0;

  color:
    #22282d;

  font-size:
    22px;

  font-weight:
    600;
}

.form-heading p {
  margin:
    7px 0 0;

  color:
    #868e94;

  font-size:
    12px;
}

.form-grid {
  display:
    grid;

  grid-template-columns:
    1fr
    1fr;

  gap:
    22px
    18px;

  margin-top:
    27px;
}

.form-grid label,
.textarea-field {
  display:
    block;
}

.form-grid .form-full {
  grid-column:
    1 / -1;
}

.form-grid label > span,
.textarea-field > span {
  display:
    block;

  margin-bottom:
    8px;

  color:
    #444b51;

  font-size:
    12px;

  font-weight:
    550;
}

.form-grid i {
  color:
    var(--orange);

  font-style:
    normal;
}

.form-grid input,
.form-grid select,
.textarea-field textarea {
  width:
    100%;

  border:
    1px solid
    #dedfdd;

  outline:
    none;

  background:
    rgba(
      255,
      255,
      255,
      .7
    );

  color:
    #343a40;

  transition:
    border-color .18s ease,
    background .18s ease;
}

.form-grid input,
.form-grid select {
  height:
    45px;

  padding:
    0 13px;

  border-radius:
    8px;

  font-size:
    12px;
}

.form-grid select {
  cursor:
    pointer;
}

.form-grid input:focus,
.form-grid select:focus,
.textarea-field textarea:focus {
  border-color:
    #aeb1ad;

  background:
    #fff;
}

.form-grid input::placeholder,
.textarea-field textarea::placeholder {
  color:
    #adb2b5;
}


/* =========================================================
   OPERATION TYPE
   ========================================================= */

.operation-field {
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: 22px 22px 24px;
  border: 1px solid #e1e3e1;
  border-radius: 16px;
  background: #fbfcfb;
}

.operation-field legend {
  max-width: 100%;
  padding: 0 10px;
  margin-left: 8px;
  color: #252b30;
  font-size: 15px;
  font-weight: 650;
  letter-spacing: -.025em;
}

.operation-field legend i {
  margin-left:
    2px;

  color:
    var(--orange);

  font-style:
    normal;
}

.operation-field legend small {
  margin-left: 7px;
  color: #92989d;
  font-size: 11px;
  font-weight: 400;
}

.operation-options {
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 16px;
}

.operation-options label {
  min-width: 0;
  cursor:
    pointer;
}

.operation-options input[type="checkbox"] {
  position:
    absolute;

  opacity:
    0;

  pointer-events:
    none;
}

.operation-options span {
  min-height: 48px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 15px;
  border: 1px solid #e0e2df;
  border-radius: 10px;
  background: #fff;
  color: #555c61;
  font-size: 13px;
  font-weight: 500;

  transition:
    border-color .18s ease,
    background .18s ease,
    color .18s ease,
    transform .18s ease;
}

.operation-options label:hover span {
  border-color:
    #bfc2be;
}

.operation-options input:checked + span {
  border-color: var(--orange);
  background: #fff7ef;
  color: #34383b;
  box-shadow: inset 3px 0 0 var(--orange);
}

.operation-options label:active span {
  transform:
    scale(
      .98
    );
}

.operation-other-input {
  width:
    100%;

  height:
    45px;

  margin-top:
    12px;

  padding:
    0 13px;

  border:
    1px solid
    #dedfdd;

  border-radius:
    8px;

  outline:
    none;

  background:
    rgba(
      255,
      255,
      255,
      .7
    );

  color:
    #343a40;

  font-size:
    12px;

  transition:
    border-color .18s ease,
    background .18s ease;
}

.operation-other-input:focus {
  border-color:
    #aeb1ad;

  background:
    #fff;
}

.operation-other-input::placeholder {
  color:
    #adb2b5;
}

.operation-error {
  margin:
    8px 0 0;

  color:
    #c45132;

  font-size:
    11px;

  line-height:
    1.5;
}


/* =========================================================
   INTEREST
   ========================================================= */

.interest-field {
  margin:
    28px 0 0;

  padding:
    0;

  border:
    0;
}

.interest-field legend {
  margin-bottom:
    11px;

  color:
    #444b51;

  font-size:
    12px;

  font-weight:
    550;
}

.interest-field legend small {
  margin-left:
    4px;

  color:
    #92989d;

  font-weight:
    400;
}

.interest-options {
  display:
    flex;

  flex-wrap:
    wrap;

  gap:
    8px;
}

.interest-options label {
  cursor:
    pointer;
}

.interest-options input {
  position:
    absolute;

  opacity:
    0;

  pointer-events:
    none;
}

.interest-options span {
  display:
    inline-flex;

  align-items:
    center;

  justify-content:
    center;

  min-height:
    34px;

  padding:
    0 15px;

  border:
    1px solid
    #d9dad7;

  border-radius:
    999px;

  background:
    rgba(
      255,
      255,
      255,
      .6
    );

  color:
    #555c61;

  font-size:
    11px;

  transition:
    all .18s ease;
}

.interest-options input:checked + span {
  border-color:
    #272d32;

  background:
    #272d32;

  color:
    #fff;
}


/* =========================================================
   TEXTAREA
   ========================================================= */

.textarea-field {
  margin-top:
    26px;
}

.textarea-field textarea {
  min-height:
    115px;

  resize:
    vertical;

  padding:
    13px;

  border-radius:
    8px;

  font-size:
    12px;

  line-height:
    1.6;
}


/* =========================================================
   CONSENT
   ========================================================= */

.consent-row {
  display:
    flex;

  align-items:
    flex-start;

  gap:
    9px;

  margin-top:
    18px;

  color:
    #777f85;

  font-size:
    11px;

  line-height:
    1.55;
}

.consent-row input {
  margin-top:
    2px;

  accent-color:
    var(--orange);
}

.consent-row a {
  color:
    #4e555b;

  font-weight:
    600;

  text-decoration:
    underline;

  text-underline-offset:
    2px;
}

.consent-row a:hover {
  color:
    var(--orange);
}


/* =========================================================
   CONSENT MODAL
   ========================================================= */

.consent-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(18, 24, 30, .42);
  backdrop-filter: blur(8px);
}

.consent-modal {
  width: min(100%, 560px);
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, .7);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 28px 80px rgba(15, 23, 42, .22);
}

.consent-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eceeec;
}

.consent-modal-kicker {
  display: block;
  margin-bottom: 8px;
  color: var(--orange);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
}

.consent-modal h3 {
  margin: 0;
  color: #22282d;
  font-size: 25px;
  font-weight: 650;
  letter-spacing: -.045em;
}

.consent-modal-close {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #f3f4f2;
  color: #667078;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.consent-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 20px;
  padding: 22px 0;
  border-bottom: 1px solid #eceeec;
}

.consent-summary div {
  min-width: 0;
}

.consent-summary span,
.consent-summary strong {
  display: block;
}

.consent-summary span {
  margin-bottom: 5px;
  color: #8a9298;
  font-size: 11px;
}

.consent-summary strong {
  overflow: hidden;
  color: #30373c;
  font-size: 14px;
  font-weight: 550;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.consent-modal-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 20px;
  color: #687178;
  font-size: 12px;
  line-height: 1.7;
}

.consent-modal-row input {
  flex: 0 0 auto;
  width: 17px;
  height: 17px;
  margin: 3px 0 0;
  accent-color: var(--orange);
}

.consent-modal-row a {
  color: #4e555b;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.consent-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 26px;
}

.consent-modal-cancel,
.consent-modal-confirm {
  min-height: 42px;
  padding: 0 18px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.consent-modal-cancel {
  background: #f1f3f1;
  color: #596268;
}

.consent-modal-confirm {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: var(--orange);
  color: #fff;
}

.consent-modal-confirm:disabled {
  opacity: .42;
  cursor: not-allowed;
}

/* =========================================================
   SUBMIT
   ========================================================= */

.form-submit-row {
  display:
    flex;

  justify-content:
    flex-end;

  margin-top:
    24px;
}

.submit-button {
  min-width:
    145px;

  height:
    45px;

  display:
    inline-flex;

  align-items:
    center;

  justify-content:
    center;

  gap:
    8px;

  padding:
    0 20px;

  border-radius:
    999px;

  background:
    var(--orange);

  color:
    #fff;

  font-size:
    13px;

  font-weight:
    600;

  cursor:
    pointer;

  transition:
    background .2s ease,
    transform .2s ease;
}

.submit-button:hover {
  background:
    var(--orange-dark);

  transform:
    translateY(
      -1px
    );
}


/* =========================================================
   SHORT DESKTOP
   ========================================================= */

@media
  (min-width: 1001px)
  and
  (max-height: 780px) {

  .contact-hero {
    height:
      400px;

    min-height:
      400px;
  }

  .hero-text h1 {
    font-size:
      clamp(
        39px,
        4vw,
        54px
      );
  }

  .hero-text p {
    margin-top:
      15px;
  }

  .hero-switch {
    margin-top:
      17px;
  }
}


/* =========================================================
   TABLET
   ========================================================= */

@media (max-width: 900px) {
  .contact-layout,
  .demo-layout {
    grid-template-columns:
      1fr;

    gap:
      45px;
  }

  .section-heading,
  .demo-intro {
    max-width:
      620px;
  }

  .contact-details {
    width:
      100%;
  }

  .hero-text-stage {
    width:
      75%;
  }
}


/* =========================================================
   MOBILE
   ========================================================= */

@media (max-width: 620px) {
  html {
    scroll-padding-top:
      56px;
  }

  .contact-wrap {
    width:
      calc(
        100% - 34px
      );
  }


  /* Hero */

  .contact-hero {
    height:
      440px;

    min-height:
      440px;
  }

  .hero-overlay {
    background:
      linear-gradient(
        90deg,
        rgba(
          5,
          12,
          20,
          .65
        ) 0%,
        rgba(
          5,
          12,
          20,
          .46
        ) 70%,
        rgba(
          5,
          12,
          20,
          .22
        ) 100%
      );
  }

  .hero-image {
    object-position:
      62%
      center;
  }

  .hero-image:nth-child(2) {
    object-position:
      64%
      center;
  }

  .hero-text-stage {
    width:
      100%;

    min-height:
      132px;
  }

  .hero-text h1 {
    font-size:
      clamp(
        37px,
        11vw,
        48px
      );
  }

  .hero-text p {
    max-width:
      330px;

    margin-top:
      15px;

    font-size:
      13px;

    line-height:
      1.7;
  }

  .hero-switch {
    gap:
      20px;

    margin-top:
      17px;
  }


  /* Contact */

  .contact-section {
    padding:
      65px 0;
  }

  .contact-line-row {
    grid-template-columns:
      44px
      1fr;

    gap:
      15px;

    min-height:
      105px;
  }

  .contact-line-main {
    grid-template-columns:
      1fr;

    gap:
      20px;
  }

  .contact-line-content {
    grid-template-columns:
      44px
      1fr;

    gap:
      15px;
  }

  .line-mark,
  .contact-simple-icon {
    width:
      42px;

    height:
      42px;
  }

  .line-qr-link {
    width:
      105px;

    height:
      105px;

    margin-left:
      59px;
  }

  .contact-value {
    font-size:
      20px;
  }

  .contact-email {
    font-size:
      16px;

    overflow-wrap:
      anywhere;
  }

  .contact-demo-callout {
    align-items:
      flex-start;

    flex-direction:
      column;

    margin-top:
      35px;
  }


  /* Demo */

  .demo-section {
    padding:
      65px 0;
  }

  .form-grid {
    grid-template-columns:
      1fr;

    gap:
      17px;
  }

  .operation-options {
    grid-template-columns: 1fr;
  }

  .operation-options span,
  .interest-options span {
    padding:
      0 12px;
  }

  .form-submit-row {
    justify-content:
      stretch;
  }

  .submit-button {
    width:
      100%;
  }
}


/* =========================================================
   REDUCED MOTION
   ========================================================= */

@media (prefers-reduced-motion: reduce) {
  .demo-web-device,
  .demo-phone-left,
  .demo-phone-right {
    animation:
      none !important;
  }

  *,
  *::before,
  *::after {
    scroll-behavior:
      auto !important;

    animation-duration:
      .001ms !important;

    transition-duration:
      .001ms !important;
  }
}
`;
